/**
 * All 33 compliance frameworks supported by SeedGovernance.
 * Doc counts sourced from the offline Packs/ directory.
 * Colors sourced from the dashboard CSS variables.
 */
export const FRAMEWORKS = {
    // ── Privacy & Data Protection ──────────────────────────────
    ndmo: {
        id: 'ndmo',
        name: 'National Data Management Office',
        shortName: 'NDMO',
        description: 'Saudi Arabia\'s national data governance framework covering data classification, sharing, open data, and institutional data management.',
        color: '#6c5ce7',
        category: 'privacy',
        docCount: 92,
        regions: ['ksa'],
        domains: ['Data Governance', 'Data Classification', 'Data Sharing', 'Open Data', 'Data Quality', 'Data Protection'],
        isHero: true,
        packsDir: 'NDMO',
    },
    pdpl: {
        id: 'pdpl',
        name: 'Personal Data Protection Law',
        shortName: 'PDPL',
        description: 'Saudi Arabia\'s personal data protection law governing collection, processing, and transfer of personal data.',
        color: '#a29bfe',
        category: 'privacy',
        docCount: 69,
        regions: ['ksa'],
        domains: ['Consent Management', 'Data Subject Rights', 'Cross-Border Transfers', 'Data Processing', 'Breach Notification'],
        isHero: true,
        packsDir: 'PDPL',
    },
    gdpr: {
        id: 'gdpr',
        name: 'General Data Protection Regulation',
        shortName: 'GDPR',
        description: 'The EU\'s comprehensive data protection regulation governing personal data processing for EU residents.',
        color: '#007bff',
        category: 'privacy',
        docCount: 40,
        regions: ['eu'],
        domains: ['Lawful Basis', 'Data Subject Rights', 'DPIAs', 'International Transfers', 'Breach Notification', 'Records of Processing'],
        isHero: true,
        packsDir: 'GDPR',
    },
    'uk-gdpr': {
        id: 'uk-gdpr',
        name: 'UK General Data Protection Regulation',
        shortName: 'UK GDPR',
        description: 'The UK\'s retained version of GDPR post-Brexit, enforced by the ICO.',
        color: '#1E88E5',
        category: 'privacy',
        docCount: 14,
        regions: ['eu'],
        domains: ['Lawful Basis', 'Data Subject Rights', 'International Transfers', 'ICO Registration'],
        isHero: false,
        packsDir: 'UK_GDPR',
    },
    ccpa: {
        id: 'ccpa',
        name: 'California Consumer Privacy Act',
        shortName: 'CCPA',
        description: 'California\'s privacy law giving consumers rights over their personal information collected by businesses.',
        color: '#26A69A',
        category: 'privacy',
        docCount: 12,
        regions: ['us'],
        domains: ['Consumer Rights', 'Notice at Collection', 'Opt-Out', 'Data Sale Disclosure', 'Privacy Policy'],
        isHero: false,
        packsDir: 'CCPA',
    },
    lgpd: {
        id: 'lgpd',
        name: 'Lei Geral de Proteção de Dados',
        shortName: 'LGPD',
        description: 'Brazil\'s general data protection law modeled after GDPR, enforced by ANPD.',
        color: '#66BB6A',
        category: 'privacy',
        docCount: 12,
        regions: ['us'],
        domains: ['Legal Basis', 'Data Subject Rights', 'DPO', 'International Transfers', 'Security Measures'],
        isHero: false,
        packsDir: 'LGPD',
    },
    'iso-27701': {
        id: 'iso-27701',
        name: 'ISO 27701 — Privacy Information Management',
        shortName: 'ISO 27701',
        description: 'Extension to ISO 27001 for privacy information management (PIMS), aligning with GDPR and other regulations.',
        color: '#6A1B9A',
        category: 'privacy',
        docCount: 14,
        regions: ['ksa', 'eu', 'us'],
        domains: ['PII Controllers', 'PII Processors', 'Privacy Risk Assessment', 'Privacy Controls'],
        isHero: false,
        packsDir: 'ISO_27701',
    },
    // ── Healthcare & Life Sciences ─────────────────────────────
    hipaa: {
        id: 'hipaa',
        name: 'Health Insurance Portability and Accountability Act',
        shortName: 'HIPAA',
        description: 'US federal law protecting sensitive patient health information from being disclosed without consent.',
        color: '#dc3545',
        category: 'healthcare',
        docCount: 37,
        regions: ['us'],
        domains: ['Privacy Rule', 'Security Rule', 'Breach Notification', 'Business Associates', 'PHI Safeguards'],
        isHero: true,
        packsDir: 'HIPAA',
    },
    fda: {
        id: 'fda',
        name: 'FDA 21 CFR Part 11',
        shortName: 'FDA',
        description: 'FDA regulations on electronic records and signatures in pharmaceutical and medical device industries.',
        color: '#28a745',
        category: 'healthcare',
        docCount: 41,
        regions: ['us'],
        domains: ['Electronic Records', 'Electronic Signatures', 'Data Integrity', 'Computer System Validation', 'Audit Trails'],
        isHero: true,
        packsDir: 'FDA',
    },
    'eu-mdr': {
        id: 'eu-mdr',
        name: 'EU Medical Device Regulation',
        shortName: 'EU MDR',
        description: 'EU regulation governing the production and distribution of medical devices in the European market.',
        color: '#43A047',
        category: 'healthcare',
        docCount: 18,
        regions: ['eu'],
        domains: ['Device Classification', 'Clinical Evaluation', 'Post-Market Surveillance', 'UDI', 'Technical Documentation'],
        isHero: false,
        packsDir: 'EU_MDR',
    },
    mhra: {
        id: 'mhra',
        name: 'MHRA Regulatory Framework',
        shortName: 'MHRA',
        description: 'UK Medicines and Healthcare products Regulatory Agency framework for medical devices and pharmaceuticals.',
        color: '#EF6C00',
        category: 'healthcare',
        docCount: 16,
        regions: ['eu'],
        domains: ['Device Registration', 'Vigilance', 'Clinical Investigations', 'GMP Compliance'],
        isHero: false,
        packsDir: 'MHRA',
    },
    gxp: {
        id: 'gxp',
        name: 'Good Practice Regulations (GxP)',
        shortName: 'GxP',
        description: 'Quality guidelines and regulations for life sciences covering GMP, GLP, GCP, and GDP.',
        color: '#F06292',
        category: 'healthcare',
        docCount: 14,
        regions: ['ksa', 'eu', 'us'],
        domains: ['GMP', 'GLP', 'GCP', 'GDP', 'Quality Management', 'Validation'],
        isHero: false,
        packsDir: 'GxP',
    },
    'ich-gcp': {
        id: 'ich-gcp',
        name: 'ICH Good Clinical Practice',
        shortName: 'ICH GCP',
        description: 'International standard for the design, conduct, and reporting of clinical trials involving human subjects.',
        color: '#7B1FA2',
        category: 'healthcare',
        docCount: 14,
        regions: ['ksa', 'eu', 'us'],
        domains: ['Clinical Trials', 'Informed Consent', 'IRB/IEC', 'Investigator Responsibilities', 'Monitoring'],
        isHero: false,
        packsDir: 'ICH_GCP',
    },
    'iso-13485': {
        id: 'iso-13485',
        name: 'ISO 13485 — Medical Device QMS',
        shortName: 'ISO 13485',
        description: 'Quality management system standard for organizations involved in medical device lifecycle.',
        color: '#00838F',
        category: 'healthcare',
        docCount: 14,
        regions: ['ksa', 'eu', 'us'],
        domains: ['Design Controls', 'Risk Management', 'Production Controls', 'CAPA', 'Supplier Management'],
        isHero: false,
        packsDir: 'ISO_13485',
    },
    // ── Information Security ───────────────────────────────────
    cybersecurity: {
        id: 'cybersecurity',
        name: 'NIST Cybersecurity Framework',
        shortName: 'Cybersecurity',
        description: 'NIST CSF for managing cybersecurity risk through Identify, Protect, Detect, Respond, and Recover functions.',
        color: '#ffc107',
        category: 'security',
        docCount: 45,
        regions: ['ksa', 'eu', 'us'],
        domains: ['Identify', 'Protect', 'Detect', 'Respond', 'Recover', 'Governance'],
        isHero: true,
        packsDir: 'Cybersecurity',
    },
    'iso-27001': {
        id: 'iso-27001',
        name: 'ISO 27001 — Information Security Management',
        shortName: 'ISO 27001',
        description: 'International standard for establishing, implementing, and maintaining an information security management system (ISMS).',
        color: '#0D47A1',
        category: 'security',
        docCount: 18,
        regions: ['ksa', 'eu', 'us'],
        domains: ['ISMS', 'Risk Assessment', 'Annex A Controls', 'Internal Audit', 'Management Review'],
        isHero: false,
        packsDir: 'ISO_27001',
    },
    'nca-ecc': {
        id: 'nca-ecc',
        name: 'NCA Essential Cybersecurity Controls',
        shortName: 'NCA ECC',
        description: 'Saudi Arabia\'s National Cybersecurity Authority essential controls for critical infrastructure protection.',
        color: '#B71C1C',
        category: 'security',
        docCount: 16,
        regions: ['ksa'],
        domains: ['Cybersecurity Governance', 'Defense', 'Resilience', 'Third-Party Security', 'Cloud Security'],
        isHero: false,
        packsDir: 'NCA_ECC',
    },
    'cis-controls': {
        id: 'cis-controls',
        name: 'CIS Controls v8',
        shortName: 'CIS Controls',
        description: 'Prioritized set of actions to protect organizations from known cyber attack vectors.',
        color: '#C62828',
        category: 'security',
        docCount: 14,
        regions: ['ksa', 'eu', 'us'],
        domains: ['Asset Management', 'Data Protection', 'Access Control', 'Vulnerability Management', 'Audit Logging'],
        isHero: false,
        packsDir: 'CIS_Controls',
    },
    'pci-dss': {
        id: 'pci-dss',
        name: 'PCI DSS v4.0',
        shortName: 'PCI DSS',
        description: 'Payment Card Industry Data Security Standard for organizations handling cardholder data.',
        color: '#FF7043',
        category: 'security',
        docCount: 14,
        regions: ['ksa', 'eu', 'us'],
        domains: ['Network Security', 'Cardholder Data', 'Access Control', 'Monitoring', 'Security Policy'],
        isHero: false,
        packsDir: 'PCI_DSS',
    },
    cmmc: {
        id: 'cmmc',
        name: 'CMMC 2.0',
        shortName: 'CMMC',
        description: 'Cybersecurity Maturity Model Certification for the US defense industrial base supply chain.',
        color: '#283593',
        category: 'security',
        docCount: 14,
        regions: ['us'],
        domains: ['Access Control', 'Incident Response', 'Risk Assessment', 'System Security', 'Audit & Accountability'],
        isHero: false,
        packsDir: 'CMMC',
    },
    'soc-2': {
        id: 'soc-2',
        name: 'SOC 2',
        shortName: 'SOC 2',
        description: 'AICPA framework for managing customer data based on trust service criteria: security, availability, processing integrity, confidentiality, privacy.',
        color: '#5C6BC0',
        category: 'security',
        docCount: 14,
        regions: ['ksa', 'eu', 'us'],
        domains: ['Security', 'Availability', 'Processing Integrity', 'Confidentiality', 'Privacy'],
        isHero: false,
        packsDir: 'SOC_2',
    },
    'nist-csf': {
        id: 'nist-csf',
        name: 'NIST Cybersecurity Framework (Extended)',
        shortName: 'NIST CSF',
        description: 'Extended NIST CSF implementation pack with additional controls and assessment guidance.',
        color: '#1565C0',
        category: 'security',
        docCount: 14,
        regions: ['us'],
        domains: ['Identify', 'Protect', 'Detect', 'Respond', 'Recover'],
        isHero: false,
        packsDir: 'NIST_CSF',
    },
    // ── IT Governance & Risk ───────────────────────────────────
    cobit: {
        id: 'cobit',
        name: 'COBIT 2019',
        shortName: 'COBIT',
        description: 'ISACA\'s framework for governance and management of enterprise information and technology.',
        color: '#4E342E',
        category: 'governance',
        docCount: 12,
        regions: ['ksa', 'eu', 'us'],
        domains: ['EDM', 'APO', 'BAI', 'DSS', 'MEA'],
        isHero: false,
        packsDir: 'COBIT',
    },
    'itil-4': {
        id: 'itil-4',
        name: 'ITIL 4',
        shortName: 'ITIL 4',
        description: 'IT service management framework for delivering IT-enabled services.',
        color: '#AD1457',
        category: 'governance',
        docCount: 12,
        regions: ['ksa', 'eu', 'us'],
        domains: ['Service Value System', 'Service Value Chain', 'Practices', 'Continual Improvement'],
        isHero: false,
        packsDir: 'ITIL_4',
    },
    'iso-31000': {
        id: 'iso-31000',
        name: 'ISO 31000 — Risk Management',
        shortName: 'ISO 31000',
        description: 'International standard providing principles and guidelines for enterprise risk management.',
        color: '#F9A825',
        category: 'governance',
        docCount: 12,
        regions: ['ksa', 'eu', 'us'],
        domains: ['Risk Assessment', 'Risk Treatment', 'Risk Monitoring', 'Risk Communication'],
        isHero: false,
        packsDir: 'ISO_31000',
    },
    'iso-22301': {
        id: 'iso-22301',
        name: 'ISO 22301 — Business Continuity',
        shortName: 'ISO 22301',
        description: 'International standard for business continuity management systems (BCMS).',
        color: '#2E7D32',
        category: 'governance',
        docCount: 12,
        regions: ['ksa', 'eu', 'us'],
        domains: ['BIA', 'BCP', 'Crisis Management', 'Recovery Strategies', 'Testing & Exercises'],
        isHero: false,
        packsDir: 'ISO_22301',
    },
    dora: {
        id: 'dora',
        name: 'Digital Operational Resilience Act',
        shortName: 'DORA',
        description: 'EU regulation on digital operational resilience for the financial sector.',
        color: '#8D6E63',
        category: 'governance',
        docCount: 12,
        regions: ['eu'],
        domains: ['ICT Risk Management', 'Incident Reporting', 'Testing', 'Third-Party Risk', 'Information Sharing'],
        isHero: false,
        packsDir: 'DORA',
    },
    sox: {
        id: 'sox',
        name: 'Sarbanes-Oxley Act',
        shortName: 'SOX',
        description: 'US federal law on corporate financial reporting, internal controls, and auditing.',
        color: '#78909C',
        category: 'governance',
        docCount: 12,
        regions: ['us'],
        domains: ['Internal Controls', 'Financial Reporting', 'Audit Committee', 'IT General Controls'],
        isHero: false,
        packsDir: 'SOX',
    },
    // ── AI & Emerging ──────────────────────────────────────────
    'iso-42001': {
        id: 'iso-42001',
        name: 'ISO 42001 — AI Management System',
        shortName: 'ISO 42001',
        description: 'International standard for establishing an AI management system, addressing risks and governance of AI systems.',
        color: '#9C27B0',
        category: 'ai',
        docCount: 12,
        regions: ['ksa', 'eu', 'us'],
        domains: ['AI Policy', 'AI Risk Assessment', 'Data Management', 'AI Lifecycle', 'Transparency'],
        isHero: false,
        packsDir: 'ISO_42001',
    },
    'nist-ai-rmf': {
        id: 'nist-ai-rmf',
        name: 'NIST AI Risk Management Framework',
        shortName: 'NIST AI RMF',
        description: 'NIST framework for managing risks associated with AI systems across the lifecycle.',
        color: '#AB47BC',
        category: 'ai',
        docCount: 12,
        regions: ['us'],
        domains: ['Govern', 'Map', 'Measure', 'Manage'],
        isHero: false,
        packsDir: 'NIST_AI',
    },
    // ── Industry Assessments ───────────────────────────────────
    'csa-caiq': {
        id: 'csa-caiq',
        name: 'CSA Consensus Assessments Initiative Questionnaire',
        shortName: 'CSA CAIQ',
        description: 'Cloud Security Alliance questionnaire for assessing cloud service provider security.',
        color: '#0277BD',
        category: 'assessment',
        docCount: 12,
        regions: ['ksa', 'eu', 'us'],
        domains: ['Application Security', 'Audit Assurance', 'BCM', 'Change Control', 'Data Security', 'Encryption'],
        isHero: false,
        packsDir: 'CSA_CAIQ',
    },
    sig: {
        id: 'sig',
        name: 'Standardized Information Gathering',
        shortName: 'SIG',
        description: 'Shared Assessments SIG questionnaire for third-party risk assessment and vendor management.',
        color: '#E65100',
        category: 'assessment',
        docCount: 12,
        regions: ['ksa', 'eu', 'us'],
        domains: ['Enterprise Risk', 'IT Risk', 'Privacy', 'Physical Security', 'Business Continuity'],
        isHero: false,
        packsDir: 'SIG',
    },
    nice: {
        id: 'nice',
        name: 'NICE Cybersecurity Workforce Framework',
        shortName: 'NICE',
        description: 'NIST framework for categorizing and describing cybersecurity work and workforce requirements.',
        color: '#00897B',
        category: 'assessment',
        docCount: 12,
        regions: ['us'],
        domains: ['Securely Provision', 'Operate & Maintain', 'Oversee & Govern', 'Protect & Defend', 'Analyze', 'Investigate'],
        isHero: false,
        packsDir: 'NICE',
    },
};
/** Ordered array of all frameworks */
export const FRAMEWORK_LIST = Object.values(FRAMEWORKS);
/** Just the 6 hero frameworks for featured sections */
export const HERO_FRAMEWORKS = FRAMEWORK_LIST.filter((f) => f.isHero);
/** Frameworks grouped by category */
export const FRAMEWORKS_BY_CATEGORY = FRAMEWORK_LIST.reduce((acc, fw) => {
    const cat = fw.category;
    if (!acc[cat])
        acc[cat] = [];
    acc[cat].push(fw);
    return acc;
}, {});
/** Category display metadata */
export const CATEGORY_META = {
    privacy: { label: 'Privacy & Data Protection', description: 'Personal data protection laws and privacy management standards' },
    healthcare: { label: 'Healthcare & Life Sciences', description: 'Regulations for medical devices, clinical trials, and healthcare data' },
    security: { label: 'Information Security', description: 'Cybersecurity frameworks and information security management standards' },
    governance: { label: 'IT Governance & Risk', description: 'Enterprise governance, risk management, and business continuity standards' },
    ai: { label: 'AI & Emerging', description: 'Standards for artificial intelligence governance and risk management' },
    assessment: { label: 'Industry Assessments', description: 'Third-party assessment questionnaires and workforce frameworks' },
};
/** Total document count across all frameworks */
export const TOTAL_DOC_COUNT = FRAMEWORK_LIST.reduce((sum, fw) => sum + fw.docCount, 0);
/** Total framework count */
export const FRAMEWORK_COUNT = FRAMEWORK_LIST.length;
//# sourceMappingURL=frameworks.js.map